// A09: Road tab — horizontal layout of Agendas + Steps with trace
// decorations. Clock-less: spacing is by order_index, not time. A
// workspace without a Road yet shows a one-click "Start main Road"
// nudge.
//
// Scope for v1:
//   - Render the main Road as a single horizontal lane.
//   - Each Agenda is a section stripe; Steps are nodes inside.
//   - Trace points for the current workspace dot each Step (turn +
//     commit for now).
//   - Add Agenda / Add Step / advance-status + close Step controls
//     inline. No drag-reorder yet (edit-mode v2).
//   - Curated vs Raw toggle defers to the existing Ledger tab; this
//     tab focuses on the Road spine itself.
//   - Parallel Roads surface as chips — first-cut selects between
//     them; convergence lands in the follow-up.

import { useEffect, useMemo, useState } from "react";
import { useTable } from "spacetimedb/react";
import { tables } from "../../../spacetimedb-bindings";
import {
  addRetroNote,
  ApiError,
  attachTaskTag,
  convergeRoad,
  createAgenda,
  createRoad,
  createStep,
  deleteAgenda,
  deleteStep,
  generateGateReceipt,
  listDriftAlerts,
  recordDecision,
  setStepStatus,
  type DriftAlertRow,
} from "../../api/client";
import { TabScrollbox } from "../../chrome/TabScrollbox";
import { ArchaeologyPanel } from "../ArchaeologyPanel";

interface Props {
  workspaceId: number;
}

interface Road {
  road_id: number;
  workspace_id: number;
  name: string;
  kind: string;
}

interface Agenda {
  agenda_id: number;
  workspace_id: number;
  road_id: number;
  title: string;
  objective: string;
  order_index: number;
}

interface Step {
  step_id: number;
  workspace_id: number;
  road_id: number;
  agenda_id: number;
  title: string;
  objective: string;
  status: string;
  order_index: number;
}

interface TracePoint {
  trace_id: number;
  step_id: number;
  kind: string;
  summary: string;
  at_micros: number;
}

function bigintToNumber(v: unknown): number {
  if (typeof v === "bigint") return Number(v);
  if (typeof v === "number") return v;
  return 0;
}

export function RoadTab({ workspaceId }: Props) {
  const [roadRows] = useTable(tables.road);
  const [agendaRows] = useTable(tables.agenda);
  const [stepRows] = useTable(tables.step);
  const [traceRows] = useTable(tables.trace_point);
  const [curatedRows] = useTable(tables.ledger_entry_curated);
  const [eventRows] = useTable(tables.event);

  const roads: Road[] = useMemo(
    () =>
      roadRows
        .filter((r) => bigintToNumber(r.workspaceId) === workspaceId)
        .map((r) => ({
          road_id: bigintToNumber(r.roadId),
          workspace_id: bigintToNumber(r.workspaceId),
          name: r.name,
          kind: r.kind,
        }))
        .sort((a, b) => {
          if (a.kind === "main" && b.kind !== "main") return -1;
          if (b.kind === "main" && a.kind !== "main") return 1;
          return a.name.localeCompare(b.name);
        }),
    [roadRows, workspaceId],
  );

  const [selectedRoadId, setSelectedRoadId] = useState<number | null>(null);
  const activeRoad =
    roads.find((r) => r.road_id === selectedRoadId) ?? roads[0] ?? null;

  const agendas: Agenda[] = useMemo(
    () =>
      activeRoad
        ? agendaRows
            .filter(
              (a) =>
                bigintToNumber(a.workspaceId) === workspaceId
                && bigintToNumber(a.roadId) === activeRoad.road_id,
            )
            .map((a) => ({
              agenda_id: bigintToNumber(a.agendaId),
              workspace_id: bigintToNumber(a.workspaceId),
              road_id: bigintToNumber(a.roadId),
              title: a.title,
              objective: a.objective,
              order_index: a.orderIndex,
            }))
            .sort((a, b) => a.order_index - b.order_index)
        : [],
    [agendaRows, workspaceId, activeRoad],
  );

  const steps: Step[] = useMemo(
    () =>
      activeRoad
        ? stepRows
            .filter(
              (s) =>
                bigintToNumber(s.workspaceId) === workspaceId
                && bigintToNumber(s.roadId) === activeRoad.road_id,
            )
            .map((s) => ({
              step_id: bigintToNumber(s.stepId),
              workspace_id: bigintToNumber(s.workspaceId),
              road_id: bigintToNumber(s.roadId),
              agenda_id: bigintToNumber(s.agendaId),
              title: s.title,
              objective: s.objective,
              status: s.status,
              order_index: s.orderIndex,
            }))
        : [],
    [stepRows, workspaceId, activeRoad],
  );

  const traceByStep: Map<number, TracePoint[]> = useMemo(() => {
    const map = new Map<number, TracePoint[]>();
    for (const row of traceRows) {
      if (bigintToNumber(row.workspaceId) !== workspaceId) continue;
      const step_id = bigintToNumber(row.stepId);
      if (!step_id) continue;
      const list = map.get(step_id) ?? [];
      list.push({
        trace_id: bigintToNumber(row.traceId),
        step_id,
        kind: row.kind,
        summary: row.summary,
        at_micros: bigintToNumber(row.atMicros),
      });
      map.set(step_id, list);
    }
    for (const list of map.values()) {
      list.sort((a, b) => b.at_micros - a.at_micros);
    }
    return map;
  }, [traceRows, workspaceId]);

  const curatedEntries = useMemo(
    () =>
      curatedRows
        .filter((r) => bigintToNumber(r.workspaceId) === workspaceId)
        .map((r) => ({
          entry_id: bigintToNumber(r.entryId),
          kind: r.kind,
          summary: r.summary,
          at_micros: bigintToNumber(r.atMicros),
        }))
        .sort((a, b) => b.at_micros - a.at_micros),
    [curatedRows, workspaceId],
  );

  const rawEntries = useMemo(
    () =>
      eventRows
        .filter((r) => bigintToNumber(r.workspaceId) === workspaceId)
        .map((r) => ({
          event_id: bigintToNumber(r.eventId),
          kind: r.kind,
          body: r.payloadJson ?? "",
          at_micros: bigintToNumber(r.createdAt),
        }))
        .sort((a, b) => b.at_micros - a.at_micros)
        .slice(0, 100),
    [eventRows, workspaceId],
  );

  if (roads.length === 0) {
    return (
      <TabScrollbox>
        <EmptyRoadCta workspaceId={workspaceId} />
        <div className="mt-8">
          <ArchaeologyPanel workspaceId={workspaceId} />
        </div>
        <LedgerPane
          workspaceId={workspaceId}
          curated={curatedEntries}
          raw={rawEntries}
        />
      </TabScrollbox>
    );
  }

  return (
    <TabScrollbox>
      <header className="mb-4">
        <p className="text-xs text-muted">
          The Road — sections (Agendas), Steps along it, and the working
          agent's trace decorating each Step. Spacing is by dependency order,
          not time.
        </p>
        <RoadDriftStrip workspaceId={workspaceId} />
        {roads.length > 1 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {roads.map((r) => (
              <button
                key={r.road_id}
                type="button"
                onClick={() => setSelectedRoadId(r.road_id)}
                className={`rounded-md border px-2 py-1 text-[0.625rem] ${
                  (activeRoad?.road_id ?? 0) === r.road_id
                    ? "border-accent text-accent"
                    : "border-edge text-muted hover:border-accent"
                }`}
              >
                {r.name}
                <span className="ml-1 text-faint">
                  {r.kind === "main" ? "· main" : "· parallel"}
                </span>
              </button>
            ))}
          </div>
        )}
      </header>
      {activeRoad && activeRoad.kind === "parallel" && (
        <ConvergeStrip workspaceId={workspaceId} road={activeRoad} />
      )}
      {activeRoad && (
        <RoadLane
          workspaceId={workspaceId}
          road={activeRoad}
          agendas={agendas}
          steps={steps}
          traceByStep={traceByStep}
        />
      )}
      <div className="mt-8">
        <ArchaeologyPanel workspaceId={workspaceId} />
      </div>
      <LedgerPane
        workspaceId={workspaceId}
        curated={curatedEntries}
        raw={rawEntries}
      />
    </TabScrollbox>
  );
}

function ConvergeStrip({
  workspaceId,
  road,
}: {
  workspaceId: number;
  road: Road;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      await convergeRoad(workspaceId, road.road_id);
      setDone(true);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      className="mb-4 flex items-center justify-between gap-2 rounded-md border border-warning/30 bg-warning/5 px-3 py-2"
      data-testid={`road-converge-${road.road_id}`}
    >
      <p className="text-[0.625rem] text-muted">
        Parallel road "{road.name}". Convergence merges its branch
        (<span className="font-code">wt-road-{road.road_id}</span>) into
        the main road's default branch via <span className="font-code">git merge --no-ff</span>.
      </p>
      {done ? (
        <span className="text-[0.625rem] text-success">Converged.</span>
      ) : (
        <button
          type="button"
          onClick={() => void run()}
          disabled={busy}
          className="shrink-0 rounded-md border border-warning px-2 py-1 text-[0.625rem] font-medium text-warning hover:bg-warning/10 disabled:opacity-50"
        >
          {busy ? "Merging…" : "Converge into main"}
        </button>
      )}
      {error && <p className="text-[0.625rem] text-danger">{error}</p>}
    </div>
  );
}

function LedgerPane({
  workspaceId,
  curated,
  raw,
}: {
  workspaceId: number;
  curated: Array<{
    entry_id: number;
    kind: string;
    summary: string;
    at_micros: number;
  }>;
  raw: Array<{
    event_id: number;
    kind: string;
    body: string;
    at_micros: number;
  }>;
}) {
  const [mode, setMode] = useState<"curated" | "raw">("curated");
  return (
    <section className="mt-8 flex flex-col gap-2">
      <header className="flex items-baseline justify-between gap-2 border-b border-edge pb-1">
        <h3 className="text-xs font-semibold text-muted">Ledger</h3>
        <div className="flex gap-1">
          <button
            type="button"
            onClick={() => setMode("curated")}
            className={`rounded-md border px-2 py-0.5 text-[0.625rem] ${
              mode === "curated"
                ? "border-accent text-accent"
                : "border-edge text-muted hover:border-accent"
            }`}
          >
            Curated
          </button>
          <button
            type="button"
            onClick={() => setMode("raw")}
            className={`rounded-md border px-2 py-0.5 text-[0.625rem] ${
              mode === "raw"
                ? "border-accent text-accent"
                : "border-edge text-muted hover:border-accent"
            }`}
          >
            Raw
          </button>
        </div>
      </header>
      {mode === "curated" ? (
        curated.length === 0 ? (
          <p className="text-[0.625rem] text-faint">
            No curated entries yet — Vela's morning pass hasn't run or
            hasn't found anything load-bearing to promote.
          </p>
        ) : (
          <ul className="flex flex-col gap-1">
            {curated.slice(0, 40).map((e) => (
              <CuratedEntryRow
                key={e.entry_id}
                workspaceId={workspaceId}
                entry={e}
              />
            ))}
          </ul>
        )
      ) : raw.length === 0 ? (
        <p className="text-[0.625rem] text-faint">No raw events yet.</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {raw.map((e) => (
            <li
              key={e.event_id}
              className="rounded-md border border-edge bg-surface-raised px-2 py-1"
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-code text-[0.625rem] text-faint">
                  {e.kind}
                </span>
                <span className="font-code text-[0.625rem] text-faint">
                  {new Date(e.at_micros / 1000).toLocaleString()}
                </span>
              </div>
              <p className="mt-0.5 whitespace-pre-wrap text-xs text-primary">
                {e.body}
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function EmptyRoadCta({ workspaceId }: { workspaceId: number }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      await createRoad(workspaceId, "Main", "main");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mx-auto mt-12 max-w-md rounded-md border border-edge bg-surface-raised px-6 py-8 text-center">
      <h2 className="mb-2 text-sm font-semibold text-primary">
        Start your main Road
      </h2>
      <p className="mb-4 text-xs text-muted">
        The Road is your workspace's start→end spine. Sections (Agendas) live
        along it; Steps are the nodes; the working agent's trace decorates
        each one. Add your first Agenda + Step after the main Road exists.
      </p>
      <button
        type="button"
        onClick={() => void start()}
        disabled={busy}
        className="rounded-md border border-accent bg-accent/10 px-3 py-1.5 text-xs font-medium text-accent hover:bg-accent/20 disabled:opacity-50"
      >
        {busy ? "Starting…" : "Start main Road"}
      </button>
      {error && <p className="mt-3 text-[0.625rem] text-danger">{error}</p>}
    </div>
  );
}

function RoadLane({
  workspaceId,
  road,
  agendas,
  steps,
  traceByStep,
}: {
  workspaceId: number;
  road: Road;
  agendas: Agenda[];
  steps: Step[];
  traceByStep: Map<number, TracePoint[]>;
}) {
  const [newAgenda, setNewAgenda] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const addAgenda = async () => {
    const title = newAgenda.trim();
    if (!title) return;
    setBusy(true);
    setError(null);
    try {
      await createAgenda(
        workspaceId,
        road.road_id,
        title,
        "",
        agendas.length,
      );
      setNewAgenda("");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={newAgenda}
          onChange={(e) => setNewAgenda(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void addAgenda();
            }
          }}
          placeholder="New agenda title…"
          className="flex-1 rounded-md border border-edge bg-surface px-2 py-1 text-xs text-primary placeholder:text-faint"
          data-testid="road-new-agenda"
        />
        <button
          type="button"
          onClick={() => void addAgenda()}
          disabled={busy || !newAgenda.trim()}
          className="rounded-md border border-accent px-2 py-1 text-[0.625rem] font-medium text-accent hover:bg-accent/10 disabled:opacity-50"
        >
          {busy ? "Adding…" : "+ Agenda"}
        </button>
      </div>
      {error && <p className="text-[0.625rem] text-danger">{error}</p>}
      {agendas.length === 0 ? (
        <p className="text-xs text-muted">
          No Agendas yet — add your first above.
        </p>
      ) : (
        <div className="flex gap-4 overflow-x-auto">
          {agendas.map((agenda) => (
            <AgendaLane
              key={agenda.agenda_id}
              workspaceId={workspaceId}
              roadId={road.road_id}
              agenda={agenda}
              steps={steps.filter((s) => s.agenda_id === agenda.agenda_id)}
              traceByStep={traceByStep}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AgendaLane({
  workspaceId,
  roadId,
  agenda,
  steps,
  traceByStep,
}: {
  workspaceId: number;
  roadId: number;
  agenda: Agenda;
  steps: Step[];
  traceByStep: Map<number, TracePoint[]>;
}) {
  const [newStep, setNewStep] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sortedSteps = [...steps].sort((a, b) => a.order_index - b.order_index);

  const addStep = async () => {
    const title = newStep.trim();
    if (!title) return;
    setBusy(true);
    setError(null);
    try {
      await createStep(workspaceId, {
        road_id: roadId,
        agenda_id: agenda.agenda_id,
        title,
        objective: "",
        order_index: sortedSteps.length,
        predecessor_ids_json: "[]",
      });
      setNewStep("");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const removeAgenda = async () => {
    try {
      await deleteAgenda(agenda.agenda_id);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  return (
    <section
      className="flex min-w-[280px] flex-col gap-2 rounded-md border border-edge bg-surface-raised p-3"
      data-testid={`road-agenda-${agenda.agenda_id}`}
    >
      <header className="flex items-center justify-between gap-2">
        <h3 className="text-xs font-semibold text-primary">{agenda.title}</h3>
        <button
          type="button"
          onClick={() => void removeAgenda()}
          className="text-[0.625rem] text-faint hover:text-danger"
          title="Delete agenda"
        >
          ×
        </button>
      </header>
      <div className="flex flex-col gap-2">
        {sortedSteps.length === 0 && (
          <p className="text-[0.625rem] text-faint">No steps yet.</p>
        )}
        {sortedSteps.map((s) => (
          <StepNode
            key={s.step_id}
            workspaceId={workspaceId}
            step={s}
            traces={traceByStep.get(s.step_id) ?? []}
          />
        ))}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={newStep}
          onChange={(e) => setNewStep(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void addStep();
            }
          }}
          placeholder="New step…"
          className="flex-1 rounded-md border border-edge bg-surface px-2 py-1 text-xs text-primary placeholder:text-faint"
          data-testid={`road-agenda-${agenda.agenda_id}-new-step`}
        />
        <button
          type="button"
          onClick={() => void addStep()}
          disabled={busy || !newStep.trim()}
          className="rounded-md border border-edge px-2 py-1 text-[0.625rem] font-medium text-muted hover:border-accent hover:text-accent disabled:opacity-50"
        >
          +
        </button>
      </div>
      {error && <p className="text-[0.625rem] text-danger">{error}</p>}
    </section>
  );
}

function StepNode({
  workspaceId,
  step,
  traces,
}: {
  workspaceId: number;
  step: Step;
  traces: TracePoint[];
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [tagDraft, setTagDraft] = useState("");
  const [tagOpen, setTagOpen] = useState(false);
  const tone =
    step.status === "closed"
      ? "border-success/60 bg-success/5"
      : step.status === "in_progress"
        ? "border-accent bg-accent/5"
        : "border-edge";
  const next =
    step.status === "open"
      ? "in_progress"
      : step.status === "in_progress"
        ? "closed"
        : null;
  const advance = async () => {
    if (!next) return;
    setBusy(true);
    setError(null);
    try {
      await setStepStatus(step.step_id, next);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  const drop = async () => {
    try {
      await deleteStep(step.step_id);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };
  const generateGate = async () => {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      await generateGateReceipt(workspaceId, step.step_id);
      void import("../../observability/posthog").then((m) =>
        m.capture("gate_receipt_generated", {
          workspace_id: workspaceId,
          step_id: step.step_id,
        }),
      );
      setInfo("Gate receipt generated.");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  const tagStep = async () => {
    const tag = tagDraft.trim();
    if (!tag) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      void import("../../observability/posthog").then((m) =>
        m.capture("task_tag_attached", {
          workspace_id: workspaceId,
          step_id: step.step_id,
          tag,
        }),
      );
      await attachTaskTag(workspaceId, {
        subject_kind: "step",
        subject_id: step.step_id,
        tag,
        source: "captain",
      });
      setInfo(`Tagged with ${tag}`);
      setTagDraft("");
      setTagOpen(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  const recordCloseDecision = async () => {
    try {
      await recordDecision(workspaceId, {
        step_id: step.step_id,
        kind: "execution",
        title: `Closed: ${step.title}`,
        body_md: `Step ${step.step_id} closed by Captain. Gate receipt should follow.`,
        evidence_refs: { step_id: step.step_id, status: "closed" },
        author_kind: "captain",
        design_archaeology: false,
      });
    } catch {
      /* non-fatal */
    }
  };
  return (
    <article
      className={`rounded-md border-l-2 px-2 py-1.5 ${tone}`}
      data-testid={`road-step-${step.step_id}`}
    >
      <div className="flex items-baseline justify-between gap-2">
        <h4 className="text-xs font-medium text-primary">{step.title}</h4>
        <span className="rounded bg-surface px-1 py-0.5 font-code text-[0.625rem] text-muted">
          {step.status.replace("_", " ")}
        </span>
      </div>
      {step.objective && (
        <p className="mt-0.5 text-[0.625rem] text-muted">{step.objective}</p>
      )}
      {traces.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {traces.slice(0, 8).map((t) => (
            <span
              key={t.trace_id}
              title={t.summary}
              className={`inline-block h-2 w-2 rounded-full ${
                t.kind === "commit"
                  ? "bg-success"
                  : t.kind === "watcher_fire"
                    ? "bg-warning"
                    : "bg-muted"
              }`}
            />
          ))}
          {traces.length > 8 && (
            <span className="text-[0.625rem] text-faint">
              +{traces.length - 8}
            </span>
          )}
        </div>
      )}
      <div className="mt-1 flex flex-wrap items-center gap-1">
        {next && (
          <button
            type="button"
            onClick={async () => {
              await advance();
              if (next === "closed") {
                await recordCloseDecision();
              }
            }}
            disabled={busy}
            className="rounded border border-edge px-1.5 py-0.5 text-[0.625rem] text-muted hover:border-accent hover:text-accent disabled:opacity-50"
          >
            {next === "in_progress" ? "Start" : "Close"}
          </button>
        )}
        <button
          type="button"
          onClick={() => void generateGate()}
          disabled={busy}
          className="rounded border border-edge px-1.5 py-0.5 text-[0.625rem] text-muted hover:border-accent hover:text-accent disabled:opacity-50"
          data-testid={`road-step-${step.step_id}-gate-receipt`}
        >
          Gate receipt
        </button>
        <button
          type="button"
          onClick={() => setTagOpen(!tagOpen)}
          className="rounded border border-edge px-1.5 py-0.5 text-[0.625rem] text-muted hover:border-accent hover:text-accent"
        >
          + tag
        </button>
        <button
          type="button"
          onClick={() => void drop()}
          className="rounded border border-edge px-1.5 py-0.5 text-[0.625rem] text-faint hover:border-danger hover:text-danger"
        >
          Drop
        </button>
      </div>
      {tagOpen && (
        <div className="mt-1 flex items-center gap-1">
          <input
            type="text"
            value={tagDraft}
            onChange={(e) => setTagDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void tagStep();
              }
            }}
            placeholder="task tag (e.g. ui-polish, implementation, research)"
            className="flex-1 rounded-md border border-edge bg-surface px-2 py-1 text-[0.625rem] text-primary"
            autoFocus
          />
          <button
            type="button"
            onClick={() => void tagStep()}
            disabled={busy || !tagDraft.trim()}
            className="rounded-md border border-accent px-2 py-0.5 text-[0.625rem] text-accent hover:bg-accent/10 disabled:opacity-50"
          >
            Tag
          </button>
        </div>
      )}
      {info && (
        <p className="mt-1 text-[0.625rem] text-success">{info}</p>
      )}
      {error && (
        <p className="mt-1 text-[0.625rem] text-danger">{error}</p>
      )}
    </article>
  );
}

function RoadDriftStrip({ workspaceId }: { workspaceId: number }) {
  const [alerts, setAlerts] = useState<DriftAlertRow[]>([]);
  useEffect(() => {
    let cancelled = false;
    listDriftAlerts(workspaceId)
      .then((a) => {
        if (!cancelled) {
          setAlerts(a.filter((r) => r.status === "open"));
        }
      })
      .catch(() => {
        /* non-fatal */
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);
  if (alerts.length === 0) return null;
  return (
    <div
      className="mt-2 rounded-md border border-warning/40 bg-warning/5 px-3 py-2"
      data-testid="road-drift-strip"
    >
      <p className="text-[0.625rem] text-warning">
        {alerts.length} open drift alert
        {alerts.length === 1 ? "" : "s"} — review on the Settings tab
        (Decision archaeology).
      </p>
    </div>
  );
}

function CuratedEntryRow({
  workspaceId,
  entry,
}: {
  workspaceId: number;
  entry: { entry_id: number; kind: string; summary: string; at_micros: number };
}) {
  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!note.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await addRetroNote(workspaceId, entry.entry_id, note.trim());
      setNote("");
      setEditing(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li
      className="rounded-md border border-edge bg-surface-raised px-2 py-1"
      data-testid={`ledger-curated-${entry.entry_id}`}
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-code text-[0.625rem] text-faint">
          {entry.kind}
        </span>
        <span className="font-code text-[0.625rem] text-faint">
          {new Date(entry.at_micros / 1000).toLocaleString()}
        </span>
      </div>
      <p className="mt-0.5 text-xs text-primary">{entry.summary}</p>
      {editing ? (
        <div className="mt-1 flex items-center gap-1">
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void submit();
              } else if (e.key === "Escape") {
                setEditing(false);
                setNote("");
              }
            }}
            placeholder="Retro note (Captain's hindsight)…"
            className="flex-1 rounded-md border border-edge bg-surface px-2 py-1 text-[0.625rem] text-primary placeholder:text-faint"
            data-testid={`ledger-curated-${entry.entry_id}-retro-input`}
            autoFocus
          />
          <button
            type="button"
            onClick={() => void submit()}
            disabled={busy || !note.trim()}
            className="rounded-md border border-accent px-2 py-0.5 text-[0.625rem] font-medium text-accent hover:bg-accent/10 disabled:opacity-50"
          >
            {busy ? "Saving…" : "Save"}
          </button>
          <button
            type="button"
            onClick={() => {
              setEditing(false);
              setNote("");
            }}
            className="rounded-md border border-edge px-2 py-0.5 text-[0.625rem] text-muted hover:text-primary"
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="mt-1 text-[0.625rem] text-faint hover:text-accent"
          data-testid={`ledger-curated-${entry.entry_id}-retro-open`}
        >
          + retro note
        </button>
      )}
      {error && <p className="mt-1 text-[0.625rem] text-danger">{error}</p>}
    </li>
  );
}
